import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import DOMPurify from 'dompurify';
import { FadeIn } from '../../FadeIn/FadeIn';
import './PortabilidadeSection.css';

const PortabilidadeSection = () => {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);

  const API_URL = process.env.REACT_APP_API_URL;

  // Sanitização segura do HTML do backend
  const SafeHTML = ({ htmlContent, style = {} }) => {
    const sanitizedHTML = DOMPurify.sanitize(htmlContent, {
      ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 'span', 'a', 'ul', 'li', 'h5'],
      ALLOWED_ATTR: ['href', 'target', 'class', 'style']
    });
    
    return (
      <div 
        style={style}
        dangerouslySetInnerHTML={{ __html: sanitizedHTML }}
      />
    );
  };

  useEffect(() => {
    const fetchCards = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API_URL}/cardPhones`);
        setCards(response.data);
      } catch (error) {
        console.error('Erro ao buscar os cards:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCards();
  }, [API_URL]);

  // Extrair texto puro do HTML para o alt da imagem
  const extractText = (html) => {
    const temp = document.createElement('div');
    temp.innerHTML = html;
    return temp.textContent || temp.innerText || '';
  };

  // Determinar ícone baseado no título/conteúdo
  const getIconForCard = (index) => {
    const icons = ['fa-exchange', 'fa-recycle', 'fa-phone', 'fa-star'];
    return icons[index % icons.length];
  };

  // Determinar cor baseada no índice (alternar entre pink e purple)
  const getColors = (index) => {
    const isEven = index % 2 === 0;
    return {
      primary: isEven ? '#F62690' : '#7A00CC',
      secondary: isEven ? '#7A00CC' : '#F62690',
      gradient: isEven 
        ? 'linear-gradient(135deg, #F62690, #7A00CC)' 
        : 'linear-gradient(135deg, #7A00CC, #F62690)',
      bg: isEven 
        ? 'linear-gradient(135deg, rgba(246, 38, 144, 0.1), rgba(122, 0, 204, 0.1))'
        : 'linear-gradient(135deg, rgba(122, 0, 204, 0.1), rgba(246, 38, 144, 0.1))',
    };
  };

  if (loading) {
    return (
      <div style={{ 
        minHeight: '400px', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        background: 'var(--bg-primary)',
      }}>
        <motion.div
          animate={{ 
            rotate: 360,
            borderColor: ['#F62690', '#7A00CC', '#F62690']
          }}
          transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
          style={{
            width: '50px',
            height: '50px',
            border: '4px solid',
            borderTopColor: 'transparent',
            borderRadius: '50%',
          }}
        />
      </div>
    );
  }

  return (
    <div style={{ 
      background: 'var(--bg-primary)', 
      padding: '6rem 0',
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* Background decorations */}
      <div style={{
        position: 'absolute',
        top: '5%',
        left: '-10%',
        width: '500px',
        height: '500px',
        background: 'radial-gradient(circle, rgba(246, 38, 144, 0.08) 0%, transparent 70%)',
        borderRadius: '50%',
        filter: 'blur(80px)',
      }} />
      <div style={{
        position: 'absolute',
        bottom: '10%',
        right: '-10%',
        width: '600px',
        height: '600px',
        background: 'radial-gradient(circle, rgba(122, 0, 204, 0.08) 0%, transparent 70%)',
        borderRadius: '50%',
        filter: 'blur(80px)',
      }} />

      <div className="container" style={{ position: 'relative', zIndex: 1 }}>
        {cards.map((card, index) => {
          const isEven = index % 2 === 0;
          const colors = getColors(index);
          const icon = getIconForCard(index);
          
          return (
            <FadeIn key={card._id} direction={isEven ? 'left' : 'right'}>
              <motion.div
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-100px' }}
                transition={{ duration: 0.6 }}
                whileHover={{ y: -5 }}
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
                  gap: '3rem',
                  alignItems: 'center',
                  marginBottom: index < cards.length - 1 ? '5rem' : '0',
                  padding: '2.5rem',
                  background: 'var(--card-bg)',
                  borderRadius: '24px',
                  border: '1px solid var(--border-color)',
                  boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
                  flexDirection: isEven ? 'row' : 'row-reverse',
                }}
              >
                {/* Conteúdo */}
                <div style={{ order: isEven ? 1 : 2 }}>
                  {/* Badge */}
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.1 }}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      padding: '0.5rem 1rem',
                      background: colors.bg,
                      borderRadius: '30px',
                      color: colors.primary,
                      fontSize: '0.8rem',
                      fontWeight: 600,
                      marginBottom: '1.25rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                    }}
                  >
                    <motion.i 
                      className={`fa ${icon}`}
                      animate={{ rotate: [0, 10, -10, 0] }}
                      transition={{ duration: 2, repeat: Infinity }}
                    />
                    {index === 0 ? 'Portabilidade' : index === 1 ? 'Compatibilidade' : index === 2 ? 'Modernidade' : 'Diferenciais'}
                  </motion.div>

                  {/* Título */}
                  <motion.h2
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.15 }}
                    style={{
                      fontSize: 'clamp(1.5rem, 3vw, 2.25rem)',
                      fontWeight: 800,
                      color: 'var(--text-primary)',
                      marginBottom: '1.25rem',
                      lineHeight: 1.2,
                      letterSpacing: '-0.02em',
                    }}
                  >
                    {card.title.split(' ').map((word, widx) => {
                      const highlightWords = ['número', 'Antigos', 'IpPhone', 'Gigabyte', 'Telecom?'];
                      const isHighlight = highlightWords.some(hw => word.includes(hw));
                      
                      return isHighlight ? (
                        <span
                          key={widx}
                          style={{
                            background: colors.gradient,
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                          }}
                        >
                          {word}{' '}
                        </span>
                      ) : (
                        <span key={widx}>{word} </span>
                      );
                    })}
                  </motion.h2>

                  {/* Lista de features - Estilizada */}
                  {card.list && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      whileInView={{ opacity: 1 }}
                      transition={{ delay: 0.2 }}
                      style={{ marginBottom: '1.5rem' }}
                    >
                      <SafeHTML 
                        htmlContent={card.list}
                        style={{
                          color: 'var(--text-primary)',
                        }}
                      />
                      <style>{`
                        .safe-list ul {
                          list-style: none;
                          padding: 0;
                          margin: 0;
                        }
                        .safe-list li {
                          position: relative;
                          padding: 0.75rem 0 0.75rem 2.5rem;
                          margin-bottom: 0.5rem;
                          border-radius: 8px;
                          transition: all 0.3s;
                        }
                        .safe-list li:hover {
                          background: var(--bg-tertiary);
                          padding-left: 2.75rem;
                        }
                        .safe-list li::before {
                          content: '✓';
                          position: absolute;
                          left: 0.75rem;
                          top: 50%;
                          transform: translateY(-50%);
                          width: 24px;
                          height: 24px;
                          background: ${colors.gradient};
                          color: white;
                          border-radius: 6px;
                          display: flex;
                          align-items: center;
                          justify-content: center;
                          font-size: 0.75rem;
                          font-weight: 700;
                        }
                      `}</style>
                    </motion.div>
                  )}

                  {/* Texto descritivo */}
                  {card.text && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      whileInView={{ opacity: 1 }}
                      transition={{ delay: 0.25 }}
                      style={{
                        color: 'var(--text-secondary)',
                        fontSize: '0.95rem',
                        lineHeight: 1.7,
                        marginBottom: '1.25rem',
                      }}
                    >
                      <SafeHTML htmlContent={card.text} />
                    </motion.div>
                  )}

                  {/* Observação/Alerta */}
                  {card.obs && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.3 }}
                      style={{
                        padding: '1rem 1.25rem',
                        background: `${colors.primary}10`,
                        border: `1px solid ${colors.primary}30`,
                        borderRadius: '12px',
                        borderLeft: `4px solid ${colors.primary}`,
                      }}
                    >
                      <div style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '0.75rem',
                      }}>
                        <i 
                          className={card.obs.includes('ATENÇÃO') || card.obs.includes('Obs:') ? 'fa fa-exclamation-circle' : 'fa fa-info-circle'}
                          style={{ 
                            color: colors.primary, 
                            marginTop: '0.2rem',
                            fontSize: '1rem',
                          }}
                        />
                        <div style={{
                          color: colors.primary,
                          fontSize: '0.875rem',
                          lineHeight: 1.6,
                          fontWeight: 500,
                        }}>
                          <SafeHTML htmlContent={card.obs} />
                        </div>
                      </div>
                    </motion.div>
                  )}
                </div>

                {/* Imagem */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.9, x: isEven ? 50 : -50 }}
                  whileInView={{ opacity: 1, scale: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  style={{
                    position: 'relative',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    order: isEven ? 2 : 1,
                    minHeight: '300px',
                  }}
                >
                  {/* Glow effect */}
                  <motion.div
                    animate={{ 
                      scale: [1, 1.1, 1],
                      opacity: [0.3, 0.5, 0.3],
                    }}
                    transition={{ duration: 4, repeat: Infinity }}
                    style={{
                      position: 'absolute',
                      width: '280px',
                      height: '280px',
                      background: `radial-gradient(circle, ${colors.primary}40 0%, transparent 70%)`,
                      borderRadius: '50%',
                      filter: 'blur(40px)',
                    }}
                  />
                  
                  {/* Decorative ring */}
                  <div style={{
                    position: 'absolute',
                    width: '320px',
                    height: '320px',
                    border: `2px dashed ${colors.primary}20`,
                    borderRadius: '50%',
                    animation: 'spin 20s linear infinite',
                  }} />

                  <motion.img
                    src={`${process.env.REACT_APP_API_IMG}/assets/cardPhones/${card.foto}`}
                    alt={extractText(card.title)}
                    style={{
                      maxWidth: '100%',
                      height: 'auto',
                      maxHeight: '350px',
                      objectFit: 'contain',
                      position: 'relative',
                      zIndex: 1,
                      filter: 'drop-shadow(0 25px 50px rgba(0,0,0,0.15))',
                    }}
                    whileHover={{ 
                      scale: 1.08,
                      rotate: isEven ? 3 : -3,
                      transition: { duration: 0.4, type: 'spring' }
                    }}
                  />

                  {/* Floating elements */}
                  <motion.div
                    animate={{ y: [0, -10, 0] }}
                    transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                    style={{
                      position: 'absolute',
                      top: '10%',
                      [isEven ? 'right' : 'left']: '5%',
                      padding: '0.5rem 1rem',
                      background: 'white',
                      borderRadius: '20px',
                      boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      zIndex: 2,
                    }}
                  >
                    <div style={{
                      width: '8px',
                      height: '8px',
                      background: colors.primary,
                      borderRadius: '50%',
                    }} />
                    <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#1f2937' }}>
                      {index === 0 ? 'Rápido' : index === 1 ? 'Econômico' : index === 2 ? 'Moderno' : 'Completo'}
                    </span>
                  </motion.div>

                  <motion.div
                    animate={{ y: [0, 10, 0] }}
                    transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", delay: 1.5 }}
                    style={{
                      position: 'absolute',
                      bottom: '15%',
                      [isEven ? 'left' : 'right']: '10%',
                      width: '50px',
                      height: '50px',
                      background: colors.gradient,
                      borderRadius: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: '1.25rem',
                      boxShadow: `0 10px 30px ${colors.primary}40`,
                      zIndex: 2,
                    }}
                  >
                    <i className={`fa ${icon}`} />
                  </motion.div>
                </motion.div>
              </motion.div>
            </FadeIn>
          );
        })}
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default PortabilidadeSection;